'use client'

import { Dialog as SheetPrimitive } from '@base-ui/react/dialog'
import { Button } from '@goaltree/ui/components/button'
import { cn } from '@goaltree/ui/lib/utils'
import { XIcon } from 'lucide-react'
import * as React from 'react'

function Sheet({ ...props }: SheetPrimitive.Root.Props) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />
}

function SheetTrigger({ ...props }: SheetPrimitive.Trigger.Props) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />
}

function SheetClose({ ...props }: SheetPrimitive.Close.Props) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />
}

function SheetPortal({ ...props }: SheetPrimitive.Portal.Props) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />
}

function SheetOverlay({ className, ...props }: SheetPrimitive.Backdrop.Props) {
  return (
    <SheetPrimitive.Backdrop
      data-slot="sheet-overlay"
      className={cn(
        'fixed inset-0 z-50 bg-black/10 text-xs/relaxed transition-opacity duration-150 data-ending-style:opacity-0 data-starting-style:opacity-0 supports-backdrop-filter:backdrop-blur-xs',
        className,
      )}
      {...props}
    />
  )
}

function SheetContent({
  className,
  children,
  side = 'right',
  showCloseButton = true,
  ...props
}: SheetPrimitive.Popup.Props & {
  side?: 'top' | 'right' | 'bottom' | 'left'
  showCloseButton?: boolean
}) {
  // Swipe-to-close (touch). We attach *native, non-passive* touch listeners to
  // the popup node via a callback ref: React's synthetic touch handlers are
  // passive, so they can't preventDefault() the browser's scroll — which is why
  // a drag would "move a bit then stop" as the browser reclaimed the gesture.
  // preventDefault() on the horizontal move keeps the drag ours; vertical moves
  // fall through to normal scrolling. Transform is set directly on the node so
  // base-ui's style handling never clobbers it.
  const axis: 'x' | 'y' = side === 'left' || side === 'right' ? 'x' : 'y'
  const closingSign = side === 'right' || side === 'bottom' ? 1 : -1
  const closeRef = React.useRef<HTMLButtonElement | null>(null)
  const detach = React.useRef<(() => void) | null>(null)

  const attachSwipe = React.useCallback(
    (el: HTMLDivElement | null) => {
      detach.current?.()
      detach.current = null
      if (!el) return
      // Left/right sheets: no swipe-to-close. The horizontal drag (and the
      // pan-y touch-action it needs) fights horizontal scrolling inside the
      // sheet — e.g. the chip rows. Close via the X button or backdrop instead.
      if (axis === 'x') return
      el.style.touchAction = 'pan-x'

      let start: { x: number; y: number } | null = null
      let mode: 'undecided' | 'drag' | 'scroll' = 'undecided'
      let offset = 0

      const onStart = (e: TouchEvent) => {
        if (e.touches.length !== 1) {
          start = null
          return
        }
        start = { x: e.touches[0].clientX, y: e.touches[0].clientY }
        mode = 'undecided'
        offset = 0
      }
      const onMove = (e: TouchEvent) => {
        if (!start || e.touches.length !== 1) return
        const dx = e.touches[0].clientX - start.x
        const dy = e.touches[0].clientY - start.y
        if (mode === 'undecided') {
          if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return
          const horizontal = Math.abs(dx) > Math.abs(dy)
          // y-axis sheet: a vertical drag closes; horizontal is normal scroll.
          mode = horizontal ? 'scroll' : 'drag'
        }
        if (mode !== 'drag') return
        const along = dy
        offset = closingSign > 0 ? Math.max(0, along) : Math.min(0, along)
        // Stop the browser from turning the drag into a scroll/back-gesture.
        if (offset !== 0 && e.cancelable) e.preventDefault()
        el.style.transition = 'none'
        el.style.transform = `translateY(${offset}px)`
      }
      const onEnd = () => {
        if (!start) return
        const wasDrag = mode === 'drag'
        const dragged = offset
        start = null
        mode = 'undecided'
        offset = 0
        if (!wasDrag) return
        const size = el.offsetHeight
        el.style.transition = '' // restore the class transition
        if (Math.abs(dragged) >= Math.max(60, size * 0.3)) {
          // Keep the dragged transform so it fades out from where it was released.
          closeRef.current?.click()
        } else {
          el.style.transform = '' // spring back
        }
      }

      el.addEventListener('touchstart', onStart, { passive: true })
      el.addEventListener('touchmove', onMove, { passive: false })
      el.addEventListener('touchend', onEnd)
      el.addEventListener('touchcancel', onEnd)
      detach.current = () => {
        el.removeEventListener('touchstart', onStart)
        el.removeEventListener('touchmove', onMove)
        el.removeEventListener('touchend', onEnd)
        el.removeEventListener('touchcancel', onEnd)
      }
    },
    [axis, closingSign],
  )

  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Popup
        ref={attachSwipe}
        data-slot="sheet-content"
        data-side={side}
        className={cn(
          axis === 'y' ? 'touch-pan-x' : 'touch-auto',
          'fixed z-50 flex flex-col bg-popover bg-clip-padding text-xs/relaxed text-popover-foreground shadow-lg transition duration-200 ease-in-out data-ending-style:opacity-0 data-starting-style:opacity-0 data-[side=bottom]:inset-x-0 data-[side=bottom]:bottom-0 data-[side=bottom]:h-auto data-[side=bottom]:border-t data-[side=bottom]:data-ending-style:translate-y-[2.5rem] data-[side=bottom]:data-starting-style:translate-y-[2.5rem] data-[side=left]:inset-y-0 data-[side=left]:left-0 data-[side=left]:h-full data-[side=left]:w-3/4 data-[side=left]:border-r data-[side=left]:data-ending-style:translate-x-[-2.5rem] data-[side=left]:data-starting-style:translate-x-[-2.5rem] data-[side=right]:inset-y-0 data-[side=right]:right-0 data-[side=right]:h-full data-[side=right]:w-3/4 data-[side=right]:border-l data-[side=right]:data-ending-style:translate-x-[2.5rem] data-[side=right]:data-starting-style:translate-x-[2.5rem] data-[side=top]:inset-x-0 data-[side=top]:top-0 data-[side=top]:h-auto data-[side=top]:border-b data-[side=top]:data-ending-style:translate-y-[-2.5rem] data-[side=top]:data-starting-style:translate-y-[-2.5rem] data-[side=left]:sm:max-w-sm data-[side=right]:sm:max-w-sm',
          className,
        )}
        {...props}
      >
        {children}
        {/* Hidden close trigger the swipe handler activates programmatically. */}
        <SheetPrimitive.Close
          ref={closeRef}
          data-slot="sheet-close"
          aria-hidden
          tabIndex={-1}
          className="hidden"
        />
        {showCloseButton && (
          <SheetPrimitive.Close
            data-slot="sheet-close"
            render={
              <Button variant="ghost" className="absolute top-1.5 right-1.5" size="icon-sm" />
            }
          >
            <XIcon />
            <span className="sr-only">Close</span>
          </SheetPrimitive.Close>
        )}
      </SheetPrimitive.Popup>
    </SheetPortal>
  )
}

function SheetHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sheet-header"
      className={cn('flex flex-col gap-0.5 p-4', className)}
      {...props}
    />
  )
}

function SheetFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sheet-footer"
      className={cn('mt-auto flex flex-col gap-2 p-4', className)}
      {...props}
    />
  )
}

function SheetTitle({ className, ...props }: SheetPrimitive.Title.Props) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn('text-sm font-medium text-foreground', className)}
      {...props}
    />
  )
}

function SheetDescription({ className, ...props }: SheetPrimitive.Description.Props) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn('text-xs/relaxed text-muted-foreground', className)}
      {...props}
    />
  )
}

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
}
